import * as React from 'react';

import {
  styled,
  styledDiv,
  useTheme,
  type Theme,
} from '@packages/utilities/styled';
import TextInput from './TextInput';
import {Copy} from './Text';
import {emotionStyled} from '@packages/utilities/styled';
import {
  SearchDefaultIcon as Search,
  CloseCircleIcon,
  IconButton,
} from '@packages/systems/spring';

const Well = styledDiv(
  ({
    theme: {colors, control},
    style,
  }: {
    theme: Theme;
    style?: {
      [key: string]: any;
    };
  }) => ({
    position: 'relative',
    borderWidth: control.borderWidth,
    boxSizing: 'border-box',
    background: (style && style.background) || colors.backgroundDeep,
    borderStyle: 'solid',
    borderColor: (style && style.borderColor) || colors.hardShadow,
    borderRadius: control.borderRadius,
  })
);

export const Wrapper = styledDiv(({theme, style}) => {
  return {
    display: 'flex',
    borderTop: theme.FinderInput.border,
    borderBottom: theme.FinderInput.border,
    borderLeft: 0,
    borderRight: 0,
    padding: theme.gutters.x8,
    position: 'relative',
    background: theme.colors.backgroundVivid,
    flexShrink: 0,
    ...style,
  };
});

export const HEIGHT = 42;

export const Input = styled(TextInput)(({theme: {gutters}, style}) => ({
  paddingLeft: gutters.x8 * 3,
  ...style,
}));

export const SearchIcon = ({
  focus,
  style = {},
  ...otherProps
}: {
  focus?: boolean;
  style?: React.CSSProperties;
} & JSX.LibraryManagedAttributes<
  typeof Search,
  React.ComponentProps<typeof Search>
>) => {
  const {gutters, control, colors} = useTheme();

  return (
    <Search
      size={12}
      {...otherProps}
      style={{
        left: gutters.x16,
        height: control.height,
        position: 'absolute',
        color: focus ? colors.foreground : colors.foregroundDimmer,
        ...style,
      }}
    />
  );
};

const ClearIconButton = emotionStyled(IconButton)({
  position: 'absolute',
  right: '$2',
  ':focus-visible': {
    outlineOffset: '-1px',
  },
});

export const CloseIconButton = ({
  onClick,
  isDisabled,
  ...props
}: {
  onClick: (arg1: React.MouseEvent<HTMLButtonElement>) => void;
  isDisabled?: boolean;
}) => (
  <ClearIconButton
    {...props}
    Icon={CloseCircleIcon}
    label="Clear search"
    variant="ghost"
    onClick={onClick}
    isDisabled={isDisabled}
  />
);

const StyledWell = styled(Well)(({theme: {gutters, colors}}) => ({
  paddingTop: gutters.x16,
  paddingBottom: gutters.x16,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: colors.backgroundVivid,
  borderColor: colors.border,
  margin: gutters.x12,
}));

export const NoResults = ({
  children,
  'data-automation-id': dataAutomationId,
}: {
  children: React.ReactNode;
  'data-automation-id'?: string;
}) => (
  <StyledWell data-automation-id={dataAutomationId || 'finder-no-results'}>
    <Copy>{children}</Copy>
  </StyledWell>
);
